import React, {
	memo,
	useCallback,
	useMemo,
	useState,
	useEffect,
	useRef,
} from "react";
import {
	DEFAULT_OFFER_IMAGE,
	DEFAULT_SHOP_IMG,
	EDIT_OFFER_ORDER_URL,
} from "../../conf";
import Surface from "./Surface";
import {
	SurfaceActionBarButton,
	SurfaceActionBarDummy,
	SurfaceActionBarItem,
	SurfaceActionOverlay,
	SurfaceExpandableCard,
	SurfaceNavBarBrandButton,
	SurfaceNavBarSection,
	SurfaceRoundedCard,
	SurfaceTitle,
} from "./SurfacesCoreUI";

import "./offer-surface.css";
import ProductViewBrandCard from "../ProductViewComponents/ProductViewBrandCard";
import {
	InlineQuantityPicker,
	Loader,
	PostPurchaseExtensionPreview,
	StatusLabel,
	Timer,
} from "../CoreUI";
import ProductVariantSelector from "../CardComponents/ProductVariantSelector";
import anim from "../../utils/anim";
// import inDesktopOnly from "../../utils/inDesktopOnly";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import GET from "../../utils/GET";
import inPreview from "../../utils/inPreview";
import useResource from "../../hooks/useResource";
import inExtensionPreview from "../../utils/inExtensionPreview";
import scrollToTarget from "../../utils/scrollToTarget";

const shopifyOrderID = GET("shopifyOrderID");
const cost = GET("cost") || "";
const userEmail = GET("email") || "";

const calcDiscountedPrice = (price, discount, percent = true) => {
	if (isNaN(parseFloat(price))) {
		return "";
	}
	if (percent) {
		return ((1 - Number(discount) / 100) * Number(price)).toFixed(2);
	}
	return Math.max(Number(price) - Number(discount), 0).toFixed(2);
};

const OfferSurfaceActionOverlay = memo(
	({
		open = false,
		product,
		selectedVariant,
		setSelectedVariant,
		discountedPrice,
		error,
		setError,
		handleClose,
	}) => {
		const handleVariantChange = useCallback(
			(value) => {
				setError(false);
				setSelectedVariant(value);
			},
			[setError, setSelectedVariant]
		);

		return (
			<SurfaceActionOverlay
				open={open}
				handleClose={handleClose}
				imgUrl={
					selectedVariant?.photo_url ||
					product?.photo_url ||
					DEFAULT_OFFER_IMAGE
				}
			>
				<section className="offer-surface-action-overlay-information-head">
					<h2 className="offer-surface-action-overlay-title">
						{product?.name}
					</h2>
					<div className="offer-surface-action-overlay-price">
						${discountedPrice}
					</div>
					{error && <StatusLabel>{error}</StatusLabel>}
				</section>

				<section className="offer-surface-action-overlay-variant-selector">
					{!product.variants && (
						<StatusLabel>
							There are no variants to display at the moment.
							Please try again later.
						</StatusLabel>
					)}
					<ProductVariantSelector
						variantData={product.variants}
						handleVariantChange={handleVariantChange}
						variants={anim.dialogItem}
					/>
				</section>
			</SurfaceActionOverlay>
		);
	}
);

const OfferSurface = memo(
	({ surfaceData, handleSurfaceClose, idx, showFullCard, rootRef }) => {
		const [showActionOverlay, setShowActionOverlay] = useState(false);
		const [purchaseError, setPurchaseError] = useState(false);
		const [selectedVariant, setSelectedVariant] = useState({});
		const [quantity, setQuantity] = useState(1);
		const [triggerDescExpand, setTriggerDescExpand] = useState(false);
		const [redirectLoading, setRedirectLoading] = useState(false);
		const track = useTrack();
		const brandDescRef = useRef(null);

		const offerRedeemDetails = useRef(null);

		const [
			{ loading, data: offerUrlData, error: offerUrlError },
			fetchOfferUrl,
			,
			setUrlOptions,
			resetUrl,
		] = useResource(
			EDIT_OFFER_ORDER_URL,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: "",
			},
			false
		);

		const {
			product,
			discount,
			percentage_discount,
			display_text: productDescriptionText = "",
			brand_description: brandDescription = "",
			remote_id: offer_id,
			cross_sell: crossSellOffer = false,
			publisher_details: brandInfo = false,
			show_timer: applyTimer,
			duration: timerDuration = 30,
			shopify_coupon_code: shopifyCouponCode = "",
			premium_credit_used: premiumCreditUsed = false,
			credits_spent: creditsSpend = 0,
			earned_credit_used: earnedCreditUsed = false,
			email_credit_used: emailCreditUsed = false,
			module_order,
			swap_id,
		} = useMemo(
			() => ({
				product: surfaceData?.products?.[0] || false,
				...surfaceData,
			}),
			[surfaceData]
		);

		const defaultTrackingProps = useMemo(
			() => ({
				offer_id: offer_id || "",
				product: product ? JSON.stringify([product]) : "",
				cross_sell_offer: crossSellOffer || false,
				module_position: idx,
				brand: brandInfo?.name || "",
				brand_id: brandInfo?.brand_id || -1,
				premium_credit_used: premiumCreditUsed || false,
				credits_spent: creditsSpend || 0,
				earned_credit_used: earnedCreditUsed || false,
				email_credit_used: emailCreditUsed || false,
				module_order,
				swap_id,
			}),
			[
				offer_id,
				product,
				crossSellOffer,
				idx,
				brandInfo,
				premiumCreditUsed,
				creditsSpend,
				earnedCreditUsed,
				emailCreditUsed,
				module_order,
				swap_id,
			]
		);

		const openActionOverlay = useCallback(
			() => setShowActionOverlay(true),
			[]
		);
		const closeActionOverlay = useCallback(() => {
			track(eventTypes.WIDGET_SURFACE_OFFER_BACK_CLICK, {
				...defaultTrackingProps,
			});
			setShowActionOverlay(false);
		}, [track, defaultTrackingProps]);

		// OFFER PROPERTIES
		const isDiscountZero = useMemo(
			() => discount !== null && Number(discount) === 0,
			[discount]
		);
		const price = useMemo(
			() => product?.discounted_price || product?.price,
			[product]
		);
		const discountedPrice = useMemo(
			() => calcDiscountedPrice(price, discount, percentage_discount),
			[discount, percentage_discount, price]
		);
		const isSingleVariant = useMemo(() => {
			if (!product?.variants) {
				return null;
			}
			return product.variants.single_variant || false;
		}, [product]);
		const showPurchaseState = useMemo(() => {
			if (showActionOverlay || isSingleVariant) {
				return true;
			}
			return false;
		}, [showActionOverlay, isSingleVariant]);

		const handleClose = useCallback(() => {
			handleSurfaceClose();
		}, [handleSurfaceClose]);

		const onExpand = useCallback(() => {}, []);

		const openProduct = useCallback(
			(orderUrl = false) => {
				if (inPreview) {
					return;
				}
				setRedirectLoading(true);
				const shopifyShop = brandInfo?.shopify_shop;

				let url = product?.landing_page || "";
				if (orderUrl) {
					url = orderUrl;
				} else if (shopifyShop && offerRedeemDetails.current.variant) {
					url = `${shopifyShop}/cart/${offerRedeemDetails.current.variant}:${offerRedeemDetails.current.quantity}?discount=${shopifyCouponCode}`;
				}
				if (!url) {
					setPurchaseError(
						"Whoops! We are unable to purchase this product at the moment. Please try again later."
					);
					setRedirectLoading(false);
					setShowActionOverlay(false);
					return;
				}

				if (window.parent) {
					return (window.parent.location.href = url);
				}
				return (window.location.href = url);
			},
			[product, brandInfo, shopifyCouponCode]
		);

		const handlePurchase = useCallback(() => {
			if (inPreview) {
				return;
			}
			track(eventTypes.WIDGET_SURFACE_OFFER_REDEEM_CLICK, {
				...defaultTrackingProps,
				display_variant_screen: showActionOverlay,
				selected_variant: selectedVariant?.variant_id || "",
				quantity: quantity || 1,
			});
			offerRedeemDetails.current = {
				variant: selectedVariant?.variant_id || false,
				quantity: quantity || 1,
			};
			if (!selectedVariant?.variant_id || crossSellOffer) {
				openProduct(false);
				return;
			}
			setUrlOptions((options) => ({
				...options,
				body: JSON.stringify({
					additions: [
						{ variant_id: selectedVariant.variant_id, quantity },
					],
					offer_id,
					cost,
					email: userEmail,
					shopify_order_id: shopifyOrderID,
					host: GET("host"),
				}),
			}));
			fetchOfferUrl();
		}, [
			track,
			defaultTrackingProps,
			selectedVariant,
			showActionOverlay,
			quantity,
			crossSellOffer,
			offer_id,
			fetchOfferUrl,
			setUrlOptions,
			openProduct,
		]);

		const handleButtonClick = useCallback(
			(dummyButtonClick = false) => {
				setPurchaseError(false);
				if (!showActionOverlay) {
					track(eventTypes.WIDGET_SURFACE_OFFER_CLICK, {
						...defaultTrackingProps,
					});
				}
				if (showPurchaseState) {
					return handlePurchase();
				}
				return openActionOverlay();
			},
			[
				track,
				defaultTrackingProps,
				showActionOverlay,
				showPurchaseState,
				handlePurchase,
				openActionOverlay,
			]
		);

		const handleDummyButtonClick = useCallback(
			() => handleButtonClick(true),
			[handleButtonClick]
		);

		const handlePurchaseClick = useCallback(
			() => handleButtonClick(false),
			[handleButtonClick]
		);

		const onTimerComplete = useCallback(() => {
			track(eventTypes.WIDGET_SURFACE_OFFER_TIMER_END, {
				...defaultTrackingProps,
			});
			handleClose();
		}, [track, defaultTrackingProps, handleClose]);

		useEffect(() => {
			if (!isSingleVariant) {
				return;
			}
			setSelectedVariant({
				variant_id: product?.variants?.variant_id || "",
			});
		}, [isSingleVariant, product]);

		useEffect(() => {
			if (!offerUrlData && !offerUrlError) {
				return;
			}
			if (offerUrlData) {
				openProduct(offerUrlData.url);
			}
			if (offerUrlError) {
				openProduct(false);
			}
			resetUrl();
		}, [offerUrlData, offerUrlError, resetUrl, openProduct]);

		const scrollToBrandDesc = useCallback(
			() => scrollToTarget(rootRef, brandDescRef),
			[rootRef]
		);

		const actionBar = useMemo(
			() => ({
				children: (
					<>
						<SurfaceActionBarItem>
							{showPurchaseState ? (
								<InlineQuantityPicker
									quantity={quantity}
									setQuantity={setQuantity}
									maxQuantity={product.max_quantity || 5}
								/>
							) : (
								<SurfaceActionBarDummy
									onClick={handleDummyButtonClick}
								/>
							)}
						</SurfaceActionBarItem>
						<SurfaceActionBarItem>
							{loading || redirectLoading ? (
								<Loader
									normal
									className="surface-action-bar-loader"
								/>
							) : (
								<SurfaceActionBarButton
									type={showPurchaseState ? "theme" : "dark"}
									onClick={handlePurchaseClick}
								>
									{showPurchaseState ? "Buy Now" : "Purchase"}
								</SurfaceActionBarButton>
							)}
						</SurfaceActionBarItem>
					</>
				),
			}),
			[
				showPurchaseState,
				loading,
				redirectLoading,
				quantity,
				setQuantity,
				handleDummyButtonClick,
				handlePurchaseClick,
				product,
			]
		);

		const navBar = useMemo(
			() => ({
				props: { product, moduleOrder: module_order },
				hidden: redirectLoading || loading,
				children:
					crossSellOffer && brandInfo ? (
						<SurfaceNavBarSection onClick={scrollToBrandDesc}>
							<SurfaceNavBarBrandButton
								heading="Exclusive offer from"
								text={brandInfo.name}
								brandInfo={brandInfo}
							/>
						</SurfaceNavBarSection>
					) : null,
			}),
			[
				product,
				brandInfo,
				crossSellOffer,
				scrollToBrandDesc,
				redirectLoading,
				loading,
				module_order,
			]
		);

		const surfaceTabContent = useMemo(
			() => (
				<>
					{applyTimer ? (
						<>
							<b>
								{
									<Timer
										valueOnly
										// duration={5}
										duration={timerDuration}
										onFinish={onTimerComplete}
										pause={
											showActionOverlay ||
											loading ||
											redirectLoading
										}
									/>
								}
							</b>{" "}
							seconds left to {!isDiscountZero && "get "}
						</>
					) : !isDiscountZero ? (
						`Hey there, you are getting `
					) : (
						""
					)}
					{!isDiscountZero ? (
						<b>
							{!percentage_discount
								? `$${discount || 0}`
								: `${discount || 0}%`}{" "}
							off
						</b>
					) : (
						"claim this offer"
					)}
				</>
			),
			[
				applyTimer,
				isDiscountZero,
				timerDuration,
				onTimerComplete,
				showActionOverlay,
				percentage_discount,
				discount,
				loading,
				redirectLoading,
			]
		);

		const descriptionCardFooter = useMemo(
			() => (
				<section className="offer-surface-description-card-footer">
					<div onClick={() => setTriggerDescExpand(true)}>
						Product Details
					</div>
					{brandInfo && crossSellOffer && (
						<>
							<div />
							<div onClick={scrollToBrandDesc}>
								<img
									src={brandInfo.logo_url || DEFAULT_SHOP_IMG}
									alt=""
								/>
								Shop Info ↓
							</div>
						</>
					)}
				</section>
			),
			[brandInfo, crossSellOffer, scrollToBrandDesc]
		);

		const displayEvent = useMemo(
			() => ({
				name: eventTypes.WIDGET_SURFACE_OFFER_DISPLAY,
				properties: {
					...defaultTrackingProps,
				},
			}),
			[defaultTrackingProps]
		);

		const skipEvent = useMemo(
			() => ({
				name: eventTypes.WIDGET_SURFACE_OFFER_SKIP,
				properties: {
					...defaultTrackingProps,
				},
			}),
			[defaultTrackingProps]
		);

		if (!product) {
			return <></>;
		}

		if (inExtensionPreview) {
			return (
				<PostPurchaseExtensionPreview
					surfaceData={{
						...surfaceData,
						discountedPrice,
						actualPrice: price,
						product,
					}}
					type="offer"
					handleClose={handleClose}
				/>
			);
		}

		return (
			<Surface
				handleClose={handleClose}
				imageView={{
					direct: true,
					imgArray: product?.supplementary_images,
					imgUrl: product?.photo_url || DEFAULT_OFFER_IMAGE,
				}}
				actionBar={actionBar}
				displayEvent={displayEvent}
				skipEvent={skipEvent}
				showFullCard={showFullCard}
				showSurfaceTab={!(isDiscountZero && !applyTimer)}
				navBar={navBar}
				surfaceTabContent={surfaceTabContent}
				className="offer-surface-container"
			>
				<SurfaceRoundedCard showFullCard={showFullCard}>
					<SurfaceTitle>{product?.name}</SurfaceTitle>
					<section className="offer-surface-price-container">
						{!isDiscountZero && (
							<span className="offer-surface-price offer-surface-price-slashed">
								${price ? price?.toFixed(2) : price}
							</span>
						)}
						<span className="offer-surface-price">
							${discountedPrice}
						</span>
					</section>
					{purchaseError && (
						<StatusLabel
							variants={anim.pdpItem}
							initial="initial"
							animate="animate"
						>
							{purchaseError}
						</StatusLabel>
					)}
					<SurfaceExpandableCard
						expandOpenFooter={descriptionCardFooter}
						triggerExpand={
							!productDescriptionText ||
							productDescriptionText.length < 100 ||
							triggerDescExpand ||
							false
						}
						disableExpandBtn
						onExpand={onExpand}
						className="offer-surface-description"
					>
						{/* {parse(product?.description || "")} */}
						{productDescriptionText}
					</SurfaceExpandableCard>
				</SurfaceRoundedCard>
				<div ref={brandDescRef} />
				{crossSellOffer && brandInfo && (
					<ProductViewBrandCard
						brand={brandInfo}
						customDescription={brandDescription}
						inSurface
					/>
				)}
				<OfferSurfaceActionOverlay
					open={showActionOverlay}
					handleClose={closeActionOverlay}
					product={product}
					discountedPrice={discountedPrice}
					selectedVariant={selectedVariant}
					setSelectedVariant={setSelectedVariant}
					error={purchaseError}
					setError={setPurchaseError}
					// quantity={quantity}
				/>
			</Surface>
		);
	}
);

export default OfferSurface;
